import { useState, useMemo } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import SearchFilters from './components/SearchFilters';
import OrderDetailModal from './components/OrderDetailModal';
import ConfirmModal from './components/ConfirmModal';
import { mockOrders } from './mockData';
import type { Order, OrderType, OrderDetail } from './types';

export default function OrderListPage() {
  const [activeTab, setActiveTab] = useState<OrderType>('desk');
  const [orders, setOrders] = useState<Order[]>(mockOrders);
  const [filters, setFilters] = useState<any>({});
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      if (order.type !== activeTab) return false;

      if (filters.searchValue) {
        const value = filters.searchValue.toLowerCase();
        const field = filters.searchType;
        if (field === 'orderNo' && !order.orderNo.toLowerCase().includes(value)) return false;
        if (field === 'paymentNo' && !order.paymentNo.toLowerCase().includes(value)) return false;
        if (field === 'productName' && !order.productName.toLowerCase().includes(value)) return false;
        if (field === 'userName' && !order.userName.toLowerCase().includes(value)) return false;
        if (field === 'userPhone' && !order.userPhone.includes(value)) return false;
      }

      if (filters.paymentMethods?.length > 0 && !filters.paymentMethods.includes(order.paymentMethod)) return false;
      if (filters.orderType && order.orderType !== filters.orderType) return false;
      if (filters.paymentChannels?.length > 0 && !filters.paymentChannels.includes(order.paymentChannel)) return false;
      if (filters.statuses?.length > 0 && !filters.statuses.includes(order.status)) return false;

      if (filters.orderTimeRange?.start && order.orderTime < filters.orderTimeRange.start.replace('T', ' ')) return false;
      if (filters.orderTimeRange?.end && order.orderTime > filters.orderTimeRange.end.replace('T', ' ')) return false;
      if (filters.paymentTimeRange?.start && order.paymentTime && order.paymentTime < filters.paymentTimeRange.start.replace('T', ' ')) return false;
      if (filters.paymentTimeRange?.end && order.paymentTime && order.paymentTime > filters.paymentTimeRange.end.replace('T', ' ')) return false;

      return true;
    });
  }, [orders, filters, activeTab]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      '待支付': 'text-orange-600 bg-orange-50',
      '已支付': 'text-blue-600 bg-blue-50',
      '已取消': 'text-gray-600 bg-gray-50',
      '已退款': 'text-red-600 bg-red-50',
      '部分退款': 'text-purple-600 bg-purple-50',
      '已完成': 'text-green-600 bg-green-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  const handleViewDetail = (order: Order) => {
    const detailOrder: OrderDetail = {
      ...order,
      configDetail: {
        productName: order.productName,
        bandwidth: '上行10M,下行10M',
        diskType: 'SSD',
        diskCapacity: '40G',
        cpu: '8核',
        memory: '16G',
        gpu: order.productName.includes('GPU') || order.productName.includes('AI') ? 'RTX3060' : '无'
      },
      discount: order.discountAmount > 0 ? {
        type: 'reduction',
        value: `${order.discountAmount}元`
      } : undefined,
      startTime: order.paymentTime ? order.paymentTime : undefined,
      endTime: order.paymentTime ? '2026-01-15 10:32:00' : undefined,
      releaseTime: order.paymentTime ? '2026-01-15 12:00:00' : undefined
    };
    setSelectedOrder(detailOrder);
  };

  const handleCancelOrder = (order: Order) => {
    setConfirmModal({
      isOpen: true,
      title: '取消订单',
      message: `确定要取消订单 ${order.orderNo} 吗？`,
      type: 'warning',
      onConfirm: () => {
        setOrders(orders.map(o => o.id === order.id ? { ...o, status: '已取消' } : o));
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleRefundOrder = (order: Order) => {
    setConfirmModal({
      isOpen: true,
      title: '订单退款',
      message: `确定要退款订单 ${order.orderNo} 吗？退款金额为 ¥${order.actualAmount.toFixed(2)}`,
      type: 'danger',
      onConfirm: () => {
        setOrders(orders.map(o => o.id === order.id ? { ...o, status: '已退款' } : o));
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleExport = () => {
    alert('导出功能开发中...');
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">订单列表</h1>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-download-line"></i>
            导出
          </button>
        </div>

        <div className="bg-white rounded-lg mb-6">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab('desk')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'desk'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Desk
              </button>
              <button
                onClick={() => setActiveTab('router')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'router'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Router
              </button>
            </div>
          </div>
        </div>

        <SearchFilters onSearch={setFilters} />

        <div className="bg-white rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">订单号</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">支付单号</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">商品名称</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">商品单价/数量</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">付费方式</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">下单类型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">订单金额</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">优惠金额</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">实收金额</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">下单用户</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">手机号码</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">下单时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">支付时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">支付方式</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">订单状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.orderNo}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.paymentNo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.productName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">¥{order.productPrice.toFixed(2)} / {order.quantity}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.paymentMethodDetail}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.orderType}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">¥{order.orderAmount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-teal-600 whitespace-nowrap">-¥{order.discountAmount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">¥{order.actualAmount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.userName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.userPhone}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.orderTime}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.paymentTime || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{order.paymentChannel}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetail(order)}
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg cursor-pointer"
                          title="查看详情"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        {order.status === '待支付' && (
                          <button
                            onClick={() => handleCancelOrder(order)}
                            className="w-8 h-8 flex items-center justify-center text-orange-600 hover:bg-orange-50 rounded-lg cursor-pointer"
                            title="取消订单"
                          >
                            <i className="ri-close-circle-line"></i>
                          </button>
                        )}
                        {order.status === '已支付' && (
                          <button
                            onClick={() => handleRefundOrder(order)}
                            className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                            title="订单退款"
                          >
                            <i className="ri-refund-line"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-4xl text-gray-300 mb-2"></i>
              <p className="text-sm text-gray-500">暂无订单数据</p>
            </div>
          )}
        </div>
      </div>

      <OrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </MainLayout>
  );
}
